import { ComponentType, lazy, LazyExoticComponent } from 'react';

// After a deploy the old chunk files are gone, so a tab still running the
// previous bundle fails every lazy import (`ChunkLoadError: Loading CSS chunk
// 4668 failed`, Sentry FRONTEND-BH). React.lazy caches the rejection, so
// "Try again" can never recover — only a full reload picks up the new
// index.html. Reload once; the timestamp guard stops a reload loop if the
// chunk is genuinely unreachable (offline, blocked CDN).
const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10_000;

export const isChunkLoadError = (error: unknown): boolean => {
  const e = error as { name?: string; message?: string } | null;
  return (
    e?.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(e?.message ?? '')
  );
};

/** Reloads the page unless it already did so within the cooldown. Returns true if it reloaded. */
export const reloadForNewBundle = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: no loop guard, so don't risk reloading forever.
    return false;
  }
  window.location.reload();
  return true;
};

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadForNewBundle()) {
        // Keep Suspense showing its fallback while the page reloads.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    })
  );
}
